import { useCallback, useEffect, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';

/**
 * Service-worker registration and the update prompt.
 *
 * `registerType: 'prompt'` means a new version WAITS rather than taking over. This hook
 * surfaces that wait as state, and `applyUpdate()` is the only thing that activates it.
 * A silent reload would be the defect: someone halfway through a leave application
 * would lose it, and the application would appear to have crashed.
 *
 * The registration is deliberately not started during tests or in development — MSW
 * owns the fetch handler there, and two service workers on one origin fight over it.
 */

let applyUpdateFn: ((reload?: boolean) => Promise<void>) | null = null;

export interface ServiceWorkerState {
  /** A new version is downloaded and waiting for permission to take over. */
  updateReady: boolean;
  /** The app is usable without a connection — the shell is cached. */
  offlineReady: boolean;
}

const listeners = new Set<(state: ServiceWorkerState) => void>();
let current: ServiceWorkerState = { updateReady: false, offlineReady: false };

function publish(next: Partial<ServiceWorkerState>): void {
  current = { ...current, ...next };
  for (const listener of listeners) listener(current);
}

/**
 * Called once from `main.tsx`.
 *
 * Returns without doing anything in development and under test, which is also why the
 * import of `virtual:pwa-register` is static: the module is stubbed by the plugin when
 * the PWA is disabled, so there is nothing to lazily avoid.
 */
export function registerServiceWorker(): void {
  if (import.meta.env.MODE === 'test') return;
  if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  applyUpdateFn = registerSW({
    immediate: true,
    onNeedRefresh: () => publish({ updateReady: true }),
    onOfflineReady: () => publish({ offlineReady: true }),
  });
}

export function useServiceWorker() {
  const [state, setState] = useState<ServiceWorkerState>(current);

  useEffect(() => {
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);

  const applyUpdate = useCallback(async () => {
    publish({ updateReady: false });
    await applyUpdateFn?.(true);
  }, []);

  const dismissUpdate = useCallback(() => publish({ updateReady: false }), []);

  return { ...state, applyUpdate, dismissUpdate };
}

/**
 * Delete every cache this origin holds.
 *
 * Called on logout. A service-worker cache survives a session and is readable by any
 * script on the origin, so on a shared machine the next person's first page load would
 * otherwise be served from the last person's cache. The API is never cached at all —
 * this clears the shell as well, which is the belt to that braces.
 */
export async function clearCaches(): Promise<void> {
  if (typeof caches === 'undefined') return;

  try {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  } catch {
    // A private window, or storage the browser has blocked. Nothing was cached there
    // either, so there is nothing to clean up.
  }
}

/** True when the app is running as an installed app rather than in a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari predates the media query and still uses this.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}
